import { useEffect, useState } from "react";
import { Header } from "../../common/Header";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { User, Mail, Phone, Calendar, MapPin } from "lucide-react";
import { User as UserType } from "@nusrif/types";
import { getUser, updateUser, getUserAvatarData } from "@nusrif/services";
export type PersonalDetails = Pick<
  UserType,
  | "firstName"
  | "lastName"
  | "email"
  | "phoneNumber"
  | "dateOfBirth"
  | "gender"
  | "location"
>;
export const PersonalDetailsPage = () => {
  const [personalDetails, setPersonalDetails] = useState<PersonalDetails>({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    dateOfBirth: "",
    gender: "",
    location: "",
  });
  const [originalDetails, setOriginalDetails] = useState<PersonalDetails>({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    dateOfBirth: "",
    gender: "",
    location: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState<UserType | null>(null);
  // Effect to fetch user details
  useEffect(() => {
    const user = getUser();
    if (user) {
      setCurrentUser(user);
      // Parse dateOfBirth from ISO DateTime to date string format for the input
      const parseDateOfBirth = (dateString?: string): string => {
        if (!dateString) return "";
        try {
          const date = new Date(dateString);
          return date.toISOString().split('T')[0]; // Convert to YYYY-MM-DD format
        } catch (error) {
          console.error("Error parsing date of birth:", error);
          return "";
        }
      };
      const details = {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phoneNumber: user.phoneNumber,
        dateOfBirth: parseDateOfBirth(user.dateOfBirth),
        gender: user.gender || "",
        location: user.location || "",
      };
      setPersonalDetails(details);
      setOriginalDetails(details); // Store original values for comparison
    }
  }, []);
  const handleInputChange = (field: keyof PersonalDetails, value: string) => {
    setPersonalDetails((prev) => ({ ...prev, [field]: value }));
  };
  const getChangedFields = (): Partial<PersonalDetails> => {
    const changedFields: Partial<PersonalDetails> = {};
    
    (Object.keys(personalDetails) as Array<keyof PersonalDetails>).forEach((key) => {
      if (personalDetails[key] !== originalDetails[key]) {
        // Convert dateOfBirth to ISO DateTime format if it's being updated
        if (key === 'dateOfBirth' && personalDetails[key]) {
          const date = new Date(personalDetails[key]);
          changedFields[key] = date.toISOString();
        } else {
          changedFields[key] = personalDetails[key];
        }
      }
    });
    
    return changedFields;
  };
  const handleSave = async () => {
    setIsLoading(true);
    try {
      const changedFields = getChangedFields();
      
      if (Object.keys(changedFields).length === 0) {
        // No changes detected
        setIsEditing(false);
        return;
      }
      await updateUser(changedFields);
      setIsEditing(false);
      
      // Update original details with new values
      setOriginalDetails(personalDetails);
      
      console.log("Personal details saved:", changedFields);
    } catch (error) {
      console.error("Error saving personal details:", error);
    } finally {
      setIsLoading(false);
    }
  };
  const handleCancel = () => {
    setIsEditing(false);
    // Reset to original values
    setPersonalDetails(originalDetails);
  };
  // Get avatar data
  const getAvatarData = () => {
    if (!currentUser) {
      return {
        initials: 'U',
        backgroundColor: 'bg-primary-blue'
      };
    }
    return getUserAvatarData(currentUser);
  };
  const avatarData = getAvatarData();
  return (
    <div className="min-h-screen w-full bg-gray-50">
      {/* Header */}
      <Header
        title="Personal Details"
        showBackButton={true}
        showCartButton={false}
      />
      {/* Main Content */}
      <div className="pt-20 px-5 space-y-6">
        {/* Profile Avatar Section */}
        <div className="flex flex-col items-center py-2">
          <div className={`w-24 h-24 rounded-full flex items-center justify-center text-white font-bold text-2xl mb-1 ${avatarData.backgroundColor}`}>
            {avatarData.initials}
          </div>
        </div>
        {/* Personal Information Form */}
        <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-black">
              Personal Information
            </h2>
            {!isEditing && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditing(true)}
                className="text-primary-blue border-primary-blue"
              >
                Edit
              </Button>
            )}
          </div>
          <div className="space-y-4">
            {/* First Name */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                First Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  value={personalDetails.firstName}
                  onChange={(e) =>
                    handleInputChange("firstName", e.target.value)
                  }
                  disabled={!isEditing}
                  className="pl-10 h-12 border-gray-300"
                />
              </div>
            </div>
            {/* Last Name */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Last Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  value={personalDetails.lastName}
                  onChange={(e) =>
                    handleInputChange("lastName", e.target.value)
                  }
                  disabled={!isEditing}
                  className="pl-10 h-12 border-gray-300"
                />
              </div>
            </div>
            {/* Email */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="email"
                  value={personalDetails.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  disabled={!isEditing}
                  className="pl-10 h-12 border-gray-300"
                />
              </div>
            </div>
            {/* Phone */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Phone Number
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="tel"
                  value={personalDetails.phoneNumber}
                  onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
                  disabled={!isEditing}
                  className="pl-10 h-12 border-gray-300"
                />
              </div>
            </div>
            {/* Date of Birth */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Date of Birth
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="date"
                  value={personalDetails.dateOfBirth}
                  onChange={(e) =>
                    handleInputChange("dateOfBirth", e.target.value)
                  }
                  disabled={!isEditing}
                  className="pl-10 h-12 border-gray-300"
                />
              </div>
            </div>
            {/* Gender */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Gender
              </label>
              <select
                value={personalDetails.gender}
                onChange={(e) => handleInputChange("gender", e.target.value)}
                disabled={!isEditing}
                className="w-full h-12 px-3 border border-gray-300 rounded-md bg-white text-black disabled:bg-gray-50"
              >
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
                <option value="Prefer not to say">Prefer not to say</option>
              </select>
            </div>
            {/* Location */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Location
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  value={personalDetails.location}
                  onChange={(e) =>
                    handleInputChange("location", e.target.value)
                  }
                  disabled={!isEditing}
                  className="pl-10 h-12 border-gray-300"
                />
              </div>
            </div>
          </div>
          {/* Action Buttons */}
          {isEditing && (
            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleCancel}
                variant="outline"
                className="flex-1 h-12"
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                className="flex-1 h-12"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  "Save Changes"
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
